import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import '../styles/MedicalConsultations.css';

const MedicalConsultations = () => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // دالة لتصحيح مسار الصور
  const getCorrectImagePath = (imagePath) => {
    if (!imagePath) return null;
    
    const baseUrl = 'https://darkslateblue-eel-151368.hostingersite.com';
    
    // إذا كان المسار يحتوي على النطاق الكامل بالفعل
    if (imagePath.startsWith('http')) {
      // إصلاح المسارات المكررة
      const corrected = imagePath.replace(
        /uploads\/consultations\/uploads\/consultations\//g,
        'uploads/consultations/'
      );
      return corrected;
    }
    
    // إذا كان المسار نسبي فقط
    if (imagePath.startsWith('uploads/')) {
      return `${baseUrl}/storage/${imagePath}`;
    }
    
    // إذا كان المسار يبدأ بـ storage/
    if (imagePath.startsWith('storage/')) {
      return `${baseUrl}/${imagePath}`;
    }
    
    // الافتراضي
    return `${baseUrl}/storage/${imagePath}`;
  };

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('https://darkslateblue-eel-151368.hostingersite.com/api/consultations', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('فشل في تحميل الاستشارات');
      }
      
      const result = await response.json();
      
      let data;
      if (result.success && result.data) {
        data = result.data;
      } else if (result.data) {
        data = result.data;
      } else if (Array.isArray(result)) {
        data = result;
      } else {
        data = [];
      }
      
      // تصحيح مسارات الصور
      if (Array.isArray(data)) {
        data = data.map(consultation => ({
          ...consultation,
          image: getCorrectImagePath(consultation.image)
        }));
      }
      
      setConsultations(Array.isArray(data) ? data : []);
      
      // تسجيل مسارات الصور للتشخيص
      if (Array.isArray(data)) {
        data.forEach((consultation, index) => {
          console.log(`Consultation ${index + 1} image URL:`, consultation.image);
        });
      }
    } catch (err) {
      setError('فشل في تحميل الاستشارات. يرجى المحاولة مرة أخرى.');
      console.error('Error fetching consultations:', err);
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  };

  // دالة لاختبار تحميل الصورة
  const testImageUrl = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchTerm(query);
  };

  const handleViewDetails = (consultationId) => {
    navigate(`/consultations/${consultationId}`);
  };

  const filteredConsultations = Array.isArray(consultations) 
    ? consultations.filter(consultation => {
        if (!searchTerm.trim()) return true;
        
        const searchLower = searchTerm.toLowerCase();
        const title = (consultation.title || '').toLowerCase();
        const description = (consultation.description || '').toLowerCase();
        const doctor = (consultation.doctor || '').toLowerCase();
        
        return title.includes(searchLower) || 
               description.includes(searchLower) || 
               doctor.includes(searchLower);
      })
    : [];

  // صورة افتراضية لاستخدامها عند فشل تحميل الصورة
  const defaultImage = 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=500';

  if (error) {
    return (
      <div className="medical-consultations-page" dir="rtl">
        <header className="consultations-header">
          <div className="header-container">
            <h1 className="header-title">الاستشارات الطبية</h1>
            <p className="header-subtitle">استشارات طبية موثوقة من أطباء متخصصين</p>
          </div>
        </header>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchConsultations} className="retry-button">
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="medical-consultations-page" dir="rtl">
      <header className="consultations-header">
        <div className="header-container">
          <h1 className="header-title">الاستشارات الطبية</h1>
          <p className="header-subtitle">استشارات طبية موثوقة من أطباء متخصصين</p>
        </div>
      </header>

      <div className="search-section">
        <div className="search-container">
          <div className="search-wrapper">
            <input
              type="text"
              placeholder="ابحث عن استشارة..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
            <Search className="search-icon" />
          </div>
        </div>
      </div>

      <div className="consultations-section">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">جاري تحميل الاستشارات...</p>
          </div>
        ) : (
          <>
            <div className="consultations-grid">
              {filteredConsultations.map(consultation => (
                <div
                  key={consultation.id}
                  className="consultation-card"
                >
                  <div className="card-image-container">
                    <img
                      src={consultation.image}
                      alt={consultation.title}
                      className="card-image"
                      onError={(e) => {
                        console.error('فشل تحميل الصورة:', consultation.image);
                        e.target.src = defaultImage;
                        
                        // محاولة استخدام مسار بديل
                        const altPath = consultation.image.replace(
                          'https://darkslateblue-eel-151368.hostingersite.com/storage/',
                          'https://darkslateblue-eel-151368.hostingersite.com/public/storage/'
                        );
                        
                        // تجربة المسار البديل بعد 100 مللي ثانية
                        setTimeout(() => {
                          const tempImg = new Image();
                          tempImg.onload = () => {
                            e.target.src = altPath;
                          };
                          tempImg.src = altPath;
                        }, 100);
                      }}
                      onLoad={(e) => {
                        console.log('تم تحميل الصورة بنجاح:', consultation.image);
                      }}
                    />
                  </div>
                  
                  <div className="card-content">
                    <h3 className="card-title">
                      {consultation.title}
                    </h3>
                    
                    {consultation.doctor && (
                      <p className="card-doctor">
                        {consultation.doctor}
                      </p>
                    )}
                    
                    
                    <button 
                      className="card-button"
                      onClick={() => handleViewDetails(consultation.id)}
                    >
                      عرض التفاصيل
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredConsultations.length === 0 && !loading && (
              <div className="no-results">
                <p className="no-results-text">
                  {searchTerm ? 'لا توجد استشارات تطابق بحثك' : 'لا توجد استشارات متاحة'}
                </p>
                {!searchTerm && (
                  <button onClick={fetchConsultations} className="retry-button">
                    إعادة التحميل
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MedicalConsultations;